import { useEffect, useState } from 'react';
import { breakpoints, type Breakpoint } from '../tokens/breakpoints';

export interface BreakpointState {
  sm: boolean;
  md: boolean;
  lg: boolean;
  xl: boolean;
  /** The largest matching breakpoint, or 'base' if below sm. */
  current: Breakpoint | 'base';
}

const BP_KEYS: readonly Breakpoint[] = ['sm', 'md', 'lg', 'xl'] as const;

function computeState(matches: Record<Breakpoint, boolean>): BreakpointState {
  // current = largest matching key; walk in reverse order
  let current: Breakpoint | 'base' = 'base';
  for (const key of BP_KEYS) {
    if (matches[key]) current = key;
  }
  return { ...matches, current };
}

function readInitial(): BreakpointState {
  // SSR-safe: if window is not available, default to no matches.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return { sm: false, md: false, lg: false, xl: false, current: 'base' };
  }
  const matches = {
    sm: window.matchMedia(`(min-width: ${breakpoints.sm}px)`).matches,
    md: window.matchMedia(`(min-width: ${breakpoints.md}px)`).matches,
    lg: window.matchMedia(`(min-width: ${breakpoints.lg}px)`).matches,
    xl: window.matchMedia(`(min-width: ${breakpoints.xl}px)`).matches,
  };
  return computeState(matches);
}

/**
 * React hook that reports which Nula breakpoints currently match.
 *
 * Mobile-first: each flag is true when viewport is AT LEAST that breakpoint
 * wide. `current` is the largest matching breakpoint (or 'base' below sm).
 *
 * SSR-safe: returns all-false on the server; updates on client mount.
 *
 * @example
 *   const bp = useBreakpoint();
 *   const panelWidth = bp.md ? 320 : '100vw';
 */
export function useBreakpoint(): BreakpointState {
  const [state, setState] = useState<BreakpointState>(readInitial);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const queries = BP_KEYS.map((key) => ({
      key,
      mql: window.matchMedia(`(min-width: ${breakpoints[key]}px)`),
    }));

    const update = () => {
      const matches = {
        sm: queries[0].mql.matches,
        md: queries[1].mql.matches,
        lg: queries[2].mql.matches,
        xl: queries[3].mql.matches,
      };
      setState(computeState(matches));
    };

    // Run once in case the value differs from the initial mount value
    // (e.g. SSR hydration or width changed between first render and effect).
    update();

    for (const { mql } of queries) mql.addEventListener('change', update);
    return () => {
      for (const { mql } of queries) mql.removeEventListener('change', update);
    };
  }, []);

  return state;
}
